import { useRef, forwardRef, useImperativeHandle } from "react";
import * as XLSX from "xlsx";
import { request } from "../helpers/api";
import { toast } from "react-toastify";
import { isNonRootFolder } from "../helpers/functions";
import PropTypes from "prop-types";

const parseDouble = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const normalized = String(value).replace(",", ".");
    const parsed = Number.parseFloat(normalized);
    return Number.isNaN(parsed) ? null : Number.parseFloat(parsed.toPrecision(10));
};

const NomenclaturesImporter = forwardRef(({ node, onImported }, ref) => {
    const fileInputRef = useRef(null);

    useImperativeHandle(ref, () => ({
        openFilePicker: () => {
            if (!isNonRootFolder(node)) {
                toast.error("Töitä voi tuoda vain alikansioihin, ei juurikansioon.");
                return;
            }
            if (fileInputRef.current) {
                fileInputRef.current.click();
            }
        },
    }));

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            const buffer = await file.arrayBuffer();
            const fileData = new Uint8Array(buffer);
            const workbook = XLSX.read(fileData, { type: "array" });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];

            const jsonData = XLSX.utils.sheet_to_json(firstSheet, {
                range: 2,
                defval: "",
            });

            const nomenclaturesToImport = jsonData
                .map((row) => ({
                    code: row.Koodi ? String(row.Koodi).trim() : null,
                    name: row.Nimi ? String(row.Nimi).trim() : null,
                    duration: parseDouble(row.Työtunnit),
                    unit: row.Yksikkö ? String(row.Yksikkö).trim() : null,
                }))
                .filter((nomenclature) => nomenclature.code?.endsWith("W"));

            if (nomenclaturesToImport.length === 0) {
                toast.warning("Ei löytynyt yhtään työtä kelvollisella koodilla.");
                return;
            }

            const registryVersion = localStorage.getItem("registryVersion");

            request(
                "nodes/importNomenclatures",
                "post",
                { parentId: node.id, nomenclatures: nomenclaturesToImport },
                { registryVersion }
            )
                .then((res) => {
                    const added = res.data.added ?? [];
                    const updated = res.data.updated ?? [];
                    const skipped = res.data.skippedDueToDuplicateCode ?? [];
                    const unchanged = res.data.unchanged ?? [];
                    const duplicatesInFile = res.data.duplicateCodesInFile ?? [];

                    if (duplicatesInFile.length > 0) {
                        const plural = duplicatesInFile.length > 1 ? "a" : "";
                        toast.warning(
                            `Tiedostossa ${duplicatesInFile.length} duplikaattikoodi${plural}: ${duplicatesInFile.join(", ")}. Tallennettu viimeisimmän rivin tiedoilla.`,
                            { autoClose: 8000 }
                        );
                    }

                    const hasNoChanges =
                        added.length === 0 && updated.length === 0 && skipped.length === 0 && unchanged.length === 0;
                    if (hasNoChanges) {
                        toast.info("Ei muutoksia.");
                        return;
                    }

                    if (skipped.length > 0) {
                        const plural = skipped.length > 1 ? "a" : "";
                        toast.warning(`${skipped.length} koodi${plural} ohitettu, koska käytössä muualla rekisterissä.`);
                    }
                    if (added.length > 0 || updated.length > 0) {
                        const unchangedSuffix = unchanged.length > 0 ? ` ${unchanged.length} ennallaan.` : "";
                        toast.success(`Tuonti valmis: ${added.length} lisätty, ${updated.length} päivitetty.${unchangedSuffix}`);
                    }
                    if (skipped.length === 0 && added.length === 0 && updated.length === 0 && unchanged.length > 0) {
                        toast.info(`${unchanged.length} työtä ennallaan, ei muutoksia.`);
                    }
                    if (added.length > 0 || updated.length > 0) {
                        onImported?.();
                    }
                })
                .catch((err) => {
                    const msg =
                        err?.response?.data?.message ??
                        err?.response?.data?.Message ??
                        "Töiden tuonti epäonnistui.";
                    console.error("ImportNomenclatures error:", err);
                    toast.error(msg);
                });
        } catch (err) {
            console.error("Error reading Excel:", err);
            toast.error("Virhe luettaessa Excel-tiedostoa: " + err.message);
        }

        event.target.value = null;
    };

    return (
        <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={handleFileChange}
        />
    );
});

NomenclaturesImporter.displayName = "NomenclaturesImporter";

NomenclaturesImporter.propTypes = {
    node: PropTypes.shape({
        id: PropTypes.string.isRequired,
        path: PropTypes.string,
    }).isRequired,
    onImported: PropTypes.func,
};

export default NomenclaturesImporter;